import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { DbTableDefinition } from 'src/app/_services/persistence/db-persistence/db-table-definition';

@Injectable({
  providedIn: 'root',
})
export class ActivityConfirmationsDbTableDefinition extends DbTableDefinition {
  tableName = 'activityConfirmations';
  tableStructure = `(id BIGINT(20) PRIMARY KEY null,
     acukey VARCHAR(255),
     label TEXT,
     acstatus INT(11),
     acislocked BOOLEAN,
     reftstamp BIGINT(20),
     eventtstamp BIGINT(20),
     ownerid BIGINT(20),
     ownerobjref VARCHAR(45),
     reportfileid BIGINT(20),
     origfileid BIGINT(20),
     dynformid BIGINT(20),
     draftdynformid BIGINT(20),
     numberrangevalue TEXT,
     draftownerempid BIGINT(20),
     draftownermcid BIGINT(20),
     draftownerservicejobid BIGINT(20),
     databag LONGTEXT
    );`;

  constructor() {
    super();
    this.updateStatements.set(47, [
      `CREATE TABLE IF NOT EXISTS ${this.tableName}${this.tableStructure}`,
    ]);
    // ACs turned out to be always backend-owned (a dynform upload response also
    // returns the complete DTO via `acdto`), so the local-only twin table this
    // originally had is obsolete. Dropped here rather than just deleted from the
    // code, so devices that already created it get cleaned up.
    this.updateStatements.set(48, [
      `DROP TABLE IF EXISTS local_activityConfirmations;`,
    ]);
    // isconflictbranch and filledformdata were briefly edited into the v47
    // CREATE above instead of getting migrations of their own. Devices that had
    // already reached v47 never re-ran that statement, so every INSERT failed
    // with "table activityConfirmations has no column named ...". Both are
    // therefore NOT part of the CREATE anymore and get added here.
    //
    // Separate versions because v49 had already shipped to test devices by the
    // time filledformdata turned out to be missing too - adding it to v49 would
    // not have re-run there. Never edit an already reached version's statements.
    //
    // Note: on devices from that era the table also still has an unused
    // `draftinfo` column (the field filledformdata replaced). Harmless, it is
    // simply never read or written.
    // `acstatus`/`acislocked` in the CREATE above were named after the legacy flat
    // keys of the dynform response. The canonical AC-DTO names are
    // `status`/`islocked`, which are added below and used exclusively - the two
    // ac-prefixed columns simply stay behind and are never read or written.
    //
    // Deliberately NOT renamed: the CREATE cannot be corrected in place (a fresh
    // install creates the table from tableStructure at version 1, see
    // DbTableDefinition, so a RENAME would find no source column there), and
    // adding instead of renaming keeps native and web on the same mechanism and
    // needs no SQLite >= 3.25.
    if (Capacitor.getPlatform() === 'web') {
      this.alterStatements.push({
        table: this.tableName,
        col: 'isconflictbranch',
        type: 'BOOLEAN',
      });
      this.alterStatements.push({
        table: this.tableName,
        col: 'filledformdata',
        type: 'LONGTEXT',
      });
      this.alterStatements.push({
        table: this.tableName,
        col: 'status',
        type: 'INT(11)',
      });
      this.alterStatements.push({
        table: this.tableName,
        col: 'islocked',
        type: 'BOOLEAN',
      });
      // isconflictbranch above was renamed to isconflictversion by the backend
      this.alterStatements.push({
        table: this.tableName,
        col: 'isconflictversion',
        type: 'BOOLEAN',
      });
      // APILevel 15 additions - see the native branch below for details
      this.alterStatements.push({
        table: this.tableName,
        col: 'jobsectionid',
        type: 'BIGINT(20)',
      });
      this.alterStatements.push({
        table: this.tableName,
        col: 'entitycreatedtstamp',
        type: 'BIGINT(20)',
      });
      this.alterStatements.push({
        table: this.tableName,
        col: 'createuser',
        type: 'VARCHAR(255)',
      });
      this.alterStatements.push({
        table: this.tableName,
        col: 'createdempid',
        type: 'BIGINT(20)',
      });
      this.alterStatements.push({
        table: this.tableName,
        col: 'createddeviceid',
        type: 'BIGINT(20)',
      });
      this.alterStatements.push({
        table: this.tableName,
        col: 'createddevicelabel',
        type: 'VARCHAR(255)',
      });
      this.alterStatements.push({
        table: this.tableName,
        col: 'previousversionactconfid',
        type: 'BIGINT(20)',
      });
      this.alterStatements.push({
        table: this.tableName,
        col: 'nextversionactconfid',
        type: 'BIGINT(20)',
      });
      // KRK-45 - see the native branch below
      this.alterStatements.push({
        table: this.tableName,
        col: 'chainrootacid',
        type: 'BIGINT(20)',
      });
    } else {
      this.updateStatements.set(49, [
        `ALTER TABLE ${this.tableName} ADD COLUMN isconflictbranch BOOLEAN;`,
      ]);
      this.updateStatements.set(50, [
        `ALTER TABLE ${this.tableName} ADD COLUMN filledformdata LONGTEXT;`,
      ]);
      this.updateStatements.set(51, [
        `ALTER TABLE ${this.tableName} ADD COLUMN status INT(11);`,
        `ALTER TABLE ${this.tableName} ADD COLUMN islocked BOOLEAN;`,
      ]);
      // The backend renamed isconflictbranch (added in v49) to isconflictversion.
      // Own version again: v49 has already run on test devices, so extending it
      // would not add the column there.
      this.updateStatements.set(52, [
        `ALTER TABLE ${this.tableName} ADD COLUMN isconflictversion BOOLEAN;`,
      ]);
      // APILevel 15: the sync now delivers the whole version chain of a document
      // (up to 50 versions) instead of only its current version. nextversionactconfid
      // is what makes a version identifiable as superseded and is therefore needed
      // for persistence, not just for display - see isCurrentVersion().
      // The create* fields carry the draft's provenance (who saved it, when, on which
      // device), which the modal shows on offered drafts.
      this.updateStatements.set(54, [
        `ALTER TABLE ${this.tableName} ADD COLUMN jobsectionid BIGINT(20);`,
        `ALTER TABLE ${this.tableName} ADD COLUMN entitycreatedtstamp BIGINT(20);`,
        `ALTER TABLE ${this.tableName} ADD COLUMN createuser VARCHAR(255);`,
        `ALTER TABLE ${this.tableName} ADD COLUMN createdempid BIGINT(20);`,
        `ALTER TABLE ${this.tableName} ADD COLUMN createdcellphoneid BIGINT(20);`,
        `ALTER TABLE ${this.tableName} ADD COLUMN createdcellphonelabel VARCHAR(255);`,
        `ALTER TABLE ${this.tableName} ADD COLUMN previousversionactconfid BIGINT(20);`,
        `ALTER TABLE ${this.tableName} ADD COLUMN nextversionactconfid BIGINT(20);`,
      ]);
      // The backend renamed createdcellphoneid/-label (added in v54 above) to
      // createddeviceid/-label before either had shipped. v54 has already run on
      // test devices though, so it must stay untouched - the two cellphone columns
      // simply join the other dead ones and are never read or written again.
      this.updateStatements.set(55, [
        `ALTER TABLE ${this.tableName} ADD COLUMN createddeviceid BIGINT(20);`,
        `ALTER TABLE ${this.tableName} ADD COLUMN createddevicelabel VARCHAR(255);`,
      ]);
      // KRK-45: the stable identity of a version chain. Needed in the table and not
      // only in the databag because the draft matching queries against it.
      this.updateStatements.set(63, [
        `ALTER TABLE ${this.tableName} ADD COLUMN chainrootacid BIGINT(20);`,
      ]);
    }
  }
}
